import React, {useEffect, useRef, useState} from "react";
import useDebounce from "../../hooks/useDebounce";

interface Option<T> {
    value: T,
    label: string
}

interface OptionListProps<T> {
    options: Option<T>[],
    onClick: (option: Option<T>) => void
}

function OptionList<T extends any>(props: OptionListProps<T>) {
    const { options, onClick } = props;

    return (
        <div className="suggestions">
            {
                options.map((option: Option<T>, index: number) =>
                    <div key={index} className="suggestion" onClick={_ => onClick(option)}>
                        {option.label}
                    </div>
                )
            }
        </div>
    )
}

interface OptionsProps<T> {
    format : (value: T) => Option<T>
    fetchDataOnChange: (query: string) => Promise<T[]>,
}

interface SearchBarProps<T> {
    name: string,
    placeholder?: string,
    disabled: boolean,
    optionsProps: OptionsProps<T>
    onSelect: (value: T) => void
    onChange: () => void;
}

function SearchBar<T>(props: SearchBarProps<T>) {
    const { placeholder, name, disabled, optionsProps, onSelect, onChange} = props;
    const { format, fetchDataOnChange } = optionsProps;

    const [options, setOptions] = useState<Option<T>[]>([]);

    const [inputText, setInputText] = useState<string>("");

    const [query, setQuery] = useState<string>("");
    const debounceQuery = useDebounce<string>(query, 500);

    const optionSelected = useRef<boolean>(false);
    const { current: currentOptionSelected } = optionSelected;

    const handleInputChange = (text: string) => {
        setInputText(text);
        setQuery(text);

        onChange();
    }

    const handleOptionClick = (option: Option<T>) => {
        const { label, value } = option;

        setOptions([]);
        setInputText(label);
        setQuery("");

        optionSelected.current = true;
        onSelect(value);
    }

    const resetSelection = () => {
        optionSelected.current = false;
    }

    useEffect(() => {
        if (currentOptionSelected) {
            return;
        }

        fetchDataOnChange(debounceQuery).then((res: T[]) => {
            return currentOptionSelected ? null : res;
        })
        .then((res: T[] | null) => {
            setOptions(res?.map(r => format(r)) ?? []);
        }).catch(_ => {
            setOptions([]);
        })
    }, [fetchDataOnChange, debounceQuery, format, currentOptionSelected])

    return (
        <div className="formContent bxd3 bxm12">
            <div className="inputDefaultContent">
                <input className="inputContent"
                       type="text"
                       name={name}
                       placeholder={placeholder}
                       disabled={disabled}
                       onChange={(e: any) => handleInputChange(e.target.value)}
                       onClick={resetSelection}
                       onFocus={resetSelection}
                       value={inputText}
                />
            </div>
            {options.length > 0 &&
                <OptionList<T> options={options} onClick={handleOptionClick}/>
            }
        </div>
    )
}

export default SearchBar;
